import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  getStudent, updateStudent, deleteStudent, getStudentFiles,
  getStudentServices, getDistricts, getAttendance, getAssessments, getIncidentReports,
  getClinicians, getSpecialEducators,
  getClinicalPlans, getAdvisoryConfig, getClassGroups, getCustomGroups,
  uploadStudentPhoto, deleteStudentPhoto, getTransportationProviders, getTransportProviderContacts,
  getStaff, getStudents, getCommunicationTypes
} from "../lib/api";
import { useAuth } from "../App";
import { StudentHeader } from "../components/students/StudentHeader";
import { ScheduleReviewBanner } from "../components/students/ScheduleReviewBanner";
import { DemographicsTab } from "../components/students/DemographicsTab";
import { TransportationTab } from "../components/students/TransportationTab";
import { EnrollmentTimeline } from "../components/students/EnrollmentTimeline";
import { EndEnrollmentDialog } from "../components/students/EndEnrollmentDialog";
import { GuardianAccessCard } from "../components/students/GuardianAccessCard";
import { CollapseProvider, CollapseAllButtons } from "../components/students/CollapsibleSection";
import { ScheduleTab } from "../components/students/ScheduleTab";
import { ClinicalPlansTab } from "../components/students/ClinicalPlansTab";
import { FilesTab } from "../components/students/FilesTab";
import ProficiencyTab from "../components/students/ProficiencyTab";
import { IncidentsTab } from "../components/students/IncidentsTab";
import { ServicesTab } from "../components/students/ServicesTab";
import { StudentAssessmentTab } from "../components/students/StudentAssessmentTab";
import { LogsTab } from "../components/students/LogsTab";
import { GoalsTab } from "../components/students/GoalsTab";
import { StudentAttendanceReportTab } from "../components/students/StudentAttendanceReportTab";
import { ReferralEditor } from "../components/referrals/ReferralEditor";
import CommunicationDialog from "../components/communications/CommunicationDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

// Color-coded tab families (echoing the Reports page). Tabs are grouped by color:
// blue = profile (Demographics/Schedule/Transportation), emerald = progress
// (Attendance/Assessments/Proficiency), violet = clinical (Clinical Plans/
// Incidents & Accidents/Logs), amber = planning (Goals/Services).
const TAB_COLORS = {
  blue: "bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white",
  emerald: "bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white",
  violet: "bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white",
  amber: "bg-amber-50 text-amber-700 data-[state=active]:bg-amber-600 data-[state=active]:text-white",
  slate: "bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white",
};

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, hasRole, hasPermission, user } = useAuth();
  
  const [student, setStudent] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [specialEducators, setSpecialEducators] = useState([]);
  const [files, setFiles] = useState([]);
  const [services, setServices] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [reports, setReports] = useState([]);
  const [accidentReports, setAccidentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [advisoryGroups, setAdvisoryGroups] = useState([]);
  const [classGroupConfig, setClassGroupConfig] = useState({ morning_groups: [], afternoon_groups: [] });
  const [clinicalPlans, setClinicalPlans] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [transportProviders, setTransportProviders] = useState([]);
  const [providerContacts, setProviderContacts] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [endEnrollOpen, setEndEnrollOpen] = useState(false);

  // Header "+ Log Communication" dialog (reuses the shared CommunicationDialog). Staff /
  // students / custom types are loaded lazily on first open; a bump to commRefreshKey
  // tells the Logs ▸ Communications tab to reload after a save.
  const canLogComm = isAdmin() || hasPermission("communications");
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commStaff, setCommStaff] = useState([]);
  const [commStudents, setCommStudents] = useState([]);
  const [commTypes, setCommTypes] = useState([]);
  const [commLoaded, setCommLoaded] = useState(false);
  const [commRefreshKey, setCommRefreshKey] = useState(0);

  const openLogComm = async () => {
    if (!commLoaded) {
      try {
        const [staffRes, studRes, typesRes] = await Promise.all([
          getStaff(), getStudents("current", true), getCommunicationTypes(),
        ]);
        setCommStaff([...(staffRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setCommStudents((studRes.data || []).filter((s) => s.status === "current"));
        setCommTypes(typesRes.data || []);
        setCommLoaded(true);
      } catch {
        toast.error("Failed to load communication form data");
      }
    }
    setCommDialogOpen(true);
  };

  const fetchData = async () => {
    try {
      const [studentRes, districtsRes, cliniciansRes, specialEducatorsRes, filesRes, servicesRes, attendanceRes, assessmentsRes, reportsRes, plansRes, advisoryRes, classGroupsRes, customGroupsRes] = await Promise.all([
        getStudent(id),
        getDistricts(),
        getClinicians(),
        getSpecialEducators(),
        getStudentFiles(id),
        getStudentServices(id),
        getAttendance({ student_id: id }),
        getAssessments({ student_id: id }),
        getIncidentReports({ student_id: id }),
        getClinicalPlans(id),
        getAdvisoryConfig(),
        getClassGroups(),
        getCustomGroups(),
      ]);
      setStudent(studentRes.data);
      setEditData(studentRes.data);
      const byName = (arr) => [...(arr || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setDistricts(byName(districtsRes.data));
      setClinicians(byName(cliniciansRes.data));
      setSpecialEducators(byName(specialEducatorsRes.data));
      setFiles(filesRes.data);
      setServices(servicesRes.data);
      setAttendance(attendanceRes.data);
      setAssessments(assessmentsRes.data);
      setReports(reportsRes.data);
      setClinicalPlans(plansRes.data);
      setAdvisoryGroups(byName(advisoryRes.data.groups || []));
      setClassGroupConfig(classGroupsRes.data);
      setCustomGroups(byName(customGroupsRes.data || []));
      getTransportationProviders().then((r) => setTransportProviders(r.data || [])).catch(() => {});
      getTransportProviderContacts().then((r) => setProviderContacts(r.data || [])).catch(() => {});
      try {
        const accRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accident-reports/student/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (accRes.ok) setAccidentReports(await accRes.json());
      } catch (e) {
        console.error("Failed to load accident reports:", e);
      }
    } catch (err) {
      toast.error("Failed to load student data");
      navigate("/students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const ENDED_STATES = ["former", "graduated"];

  const doSave = async (extra = {}) => {
    try {
      const allowedFields = [
        'first_name', 'last_name', 'middle_name', 'preferred_name', 'pronouns', 'dob', 'grade',
        'first_contact_relationship', 'first_contact_name', 'first_contact_phone', 'first_contact_email',
        'second_contact_relationship', 'second_contact_name', 'second_contact_phone', 'second_contact_email',
        'mailing_address', 'transport_address', 'transport_name', 'transportation_provider', 'second_transportation_provider', 'pickup_time', 'dropoff_time',
        'mailing_street', 'mailing_city', 'mailing_state', 'mailing_zip', 'mailing_notes',
        'other_address', 'other_address_name', 'school_district', 'district_clinician', 'district_special_educator',
        'assigned_clinician', 'assigned_special_educator', 'photos_allowed', 'otc_medications', 'otc_notes',
        'photos_allowed_sign_now', 'otc_medications_sign_now',
        'morning_group', 'afternoon_group', 'advisory_group', 'schedule', 'schedule_type',
        'start_date', 'end_date', 'status', 'attending_summer_school',
        'exit_reason', 'exit_reason_other', 'departure_notes',
        'student_email', 'chromebook_number', 'student_password', 'parent_account',
        'iep_link', 'supporting_docs_link', 'referrals_link',
      ];
      const payload = {};
      for (const key of allowedFields) {
        if (key in editData) payload[key] = editData[key];
      }
      Object.assign(payload, extra); // end_date / leaving_reason from the prompt
      await updateStudent(id, payload);
      const refreshed = await getStudent(id);
      setStudent(refreshed.data);
      setEditData(refreshed.data);
      setEditing(false);
      setEndEnrollOpen(false);
      toast.success("Student updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update student");
    }
  };

  const handleSave = async () => {
    // When a student is newly marked Former/Graduated, prompt the admin to
    // confirm/enter the end date + reason for the current enrollment period.
    const wasActive = !ENDED_STATES.includes((student?.status || "current").toLowerCase());
    const nowEnded = ENDED_STATES.includes((editData?.status || "").toLowerCase());
    if (wasActive && nowEnded) {
      setEndEnrollOpen(true);
      return;
    }
    await doSave();
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await deleteStudent(id);
      toast.success("Student deleted");
      navigate("/students");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete student");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo must be under 2MB");
      e.target.value = "";
      return;
    }
    setPhotoUploading(true);
    try {
      await uploadStudentPhoto(id, file);
      const res = await getStudent(id);
      setStudent(res.data);
      setEditData(res.data);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm("Remove this student's photo?")) return;
    try {
      await deleteStudentPhoto(id);
      const res = await getStudent(id);
      setStudent(res.data);
      setEditData(res.data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Failed to remove photo");
    }
  };

  const formatDOB = (dateStr) => {
    if (!dateStr) return "Not set";
    try {
      let d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d.getTime())) d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!student) return null;

  return (
    <div className="space-y-6" data-testid="student-detail-page">
      <StudentHeader
        student={student} editing={editing} setEditing={setEditing}
        editData={editData} setEditData={setEditData}
        handleSave={handleSave} handleDelete={handleDelete}
        handlePhotoUpload={handlePhotoUpload} handlePhotoDelete={handlePhotoDelete}
        photoUploading={photoUploading} classGroupConfig={classGroupConfig}
        formatDOB={formatDOB} isAdmin={isAdmin} navigate={navigate}
        clinicianName={(clinicians.find((c) => c.id === student.assigned_clinician) || {}).name || student.assigned_clinician || ""}
        canLogComm={canLogComm} onLogCommunication={openLogComm}
      />

      <ScheduleReviewBanner
        student={student}
        canManage={isAdmin() || hasRole("Clinician")}
        onChanged={fetchData}
      />

      <Tabs
        value={searchParams.get("tab") || "demographics"}
        onValueChange={(v) => setSearchParams((prev) => { prev.set("tab", v); return prev; }, { replace: true })}
        className="space-y-6"
      >
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          {/* Group 1 (blue): Demographics · Schedule · Transportation */}
          <TabsTrigger value="demographics" data-testid="tab-demographics" className={TAB_COLORS.blue}>Demographics</TabsTrigger>
          {student?.status === "referral" && <TabsTrigger value="referral" data-testid="tab-referral" className={TAB_COLORS.slate}>Referral</TabsTrigger>}
          <TabsTrigger value="schedule" data-testid="tab-schedule" className={TAB_COLORS.blue}>Schedule</TabsTrigger>
          <TabsTrigger value="transportation" data-testid="tab-transportation" className={TAB_COLORS.blue}>Transportation</TabsTrigger>
          {/* Group 2 (emerald): Attendance · Assessments · Proficiency */}
          <TabsTrigger value="attendance" data-testid="tab-attendance" className={TAB_COLORS.emerald}>Attendance</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments" className={TAB_COLORS.emerald}>Assessments</TabsTrigger>
          <TabsTrigger value="proficiency" data-testid="tab-proficiency" className={TAB_COLORS.emerald}>Proficiency</TabsTrigger>
          {/* Group 3 (violet): Clinical Plans · Incidents & Accidents · Logs */}
          <TabsTrigger value="clinical-plans" data-testid="tab-clinical-plans" className={TAB_COLORS.violet}>Clinical Plans</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents" className={TAB_COLORS.violet}>Incidents & Accidents</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs" className={TAB_COLORS.violet}>Logs</TabsTrigger>
          {/* Group 4 (amber): Goals · Services */}
          <TabsTrigger value="goals" data-testid="tab-goals" className={TAB_COLORS.amber}>Goals</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services" className={TAB_COLORS.amber}>Services</TabsTrigger>
          {/* Reference documents */}
          <TabsTrigger value="files" data-testid="tab-files" className={TAB_COLORS.slate}>Stored Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="demographics" className="space-y-6">
          <CollapseProvider defaultCollapsed>
            <div className="flex justify-end">
              <CollapseAllButtons />
            </div>
            <DemographicsTab
              student={student} editing={editing} editData={editData} setEditData={setEditData}
              districts={districts} clinicians={clinicians} specialEducators={specialEducators}
              advisoryGroups={advisoryGroups} classGroupConfig={classGroupConfig} customGroups={customGroups}
            />
            <EnrollmentTimeline periods={student?.enrollment_periods} />
            {isAdmin() && student?.id && <GuardianAccessCard studentId={student.id} />}
          </CollapseProvider>
        </TabsContent>

        <TabsContent value="transportation" className="space-y-6">
          <TransportationTab
            student={student} editing={editing} editData={editData} setEditData={setEditData}
            transportProviders={transportProviders} providerContacts={providerContacts}
          />
        </TabsContent>

        {student?.status === "referral" && (
          <TabsContent value="referral" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Referral Pipeline</CardTitle>
                <CardDescription>Track this candidate through the admissions process. Tour, Class Visit, and Interview dates sync to the staff calendar.</CardDescription>
              </CardHeader>
              <CardContent>
                <ReferralEditor referral={student} onSaved={(updated) => setStudent(updated)} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="schedule" className="space-y-6">
          <ScheduleTab student={student} setStudent={setStudent} studentId={id} onGoToAttendance={() => setSearchParams((prev) => { prev.set("tab", "attendance"); return prev; }, { replace: true })} />
        </TabsContent>

        <TabsContent value="attendance" className="space-y-6" data-testid="attendance-report-tab">
          <StudentAttendanceReportTab student={student} districts={districts} />
        </TabsContent>

        <TabsContent value="clinical-plans" className="space-y-6">
          <ClinicalPlansTab clinicalPlans={clinicalPlans} setClinicalPlans={setClinicalPlans} studentId={id} />
        </TabsContent>

        <TabsContent value="goals" className="space-y-6" data-testid="goals-tab-content">
          <GoalsTab studentId={id} />
        </TabsContent>

        <TabsContent value="files" className="space-y-6">
          <FilesTab
            student={student} editing={editing} editData={editData} setEditData={setEditData}
            files={files} setFiles={setFiles} assessments={assessments} studentId={id}
          />
        </TabsContent>

        <TabsContent value="incidents" className="space-y-6">
          <IncidentsTab student={student} reports={reports.filter((r) => r.type !== "general_note")} accidentReports={accidentReports} hasRole={hasRole} onUpdated={fetchData} />
        </TabsContent>

        <TabsContent value="services" className="space-y-6">
          <ServicesTab services={services} setServices={setServices} studentId={id} />
        </TabsContent>

        <TabsContent value="assessments" className="space-y-6" data-testid="assessments-tab">
          <StudentAssessmentTab studentId={id} />
        </TabsContent>

        <TabsContent value="proficiency" className="space-y-6" data-testid="proficiency-tab-content">
          <ProficiencyTab studentId={id} />
        </TabsContent>

        <TabsContent value="logs" className="space-y-6" data-testid="logs-tab">
          <LogsTab
            studentId={id}
            studentName={`${student.preferred_name || student.first_name} ${student.last_name}`}
            generalNotes={reports.filter((r) => r.type === "general_note")}
            userId={user?.id}
            onUpdated={fetchData}
            commRefreshKey={commRefreshKey}
          />
        </TabsContent>
      </Tabs>

      {canLogComm && (
        <CommunicationDialog
          open={commDialogOpen}
          onOpenChange={setCommDialogOpen}
          editing={null}
          staff={commStaff}
          students={commStudents}
          customTypes={commTypes}
          defaultStudentId={id}
          onSaved={() => {
            setCommDialogOpen(false);
            setCommRefreshKey((k) => k + 1);
            getCommunicationTypes().then((r) => setCommTypes(r.data)).catch(() => {});
          }}
        />
      )}

      <EndEnrollmentDialog
        open={endEnrollOpen}
        onOpenChange={setEndEnrollOpen}
        studentName={student ? `${student.preferred_name || student.first_name} ${student.last_name}` : ""}
        defaultEndDate={editData?.end_date}
        defaultReason={editData?.status === "graduated" ? "Graduated" : ""}
        onConfirm={(extra) => doSave(extra)}
      />
    </div>
  );
}
